import images from '@assets/images/index'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import {
  GetAPIparams,
  calculateSummary,
  getFixedValue,
  useDropdownMenu
} from '@utils/assessmentReportUtils'
import { Flex, Spin } from 'antd'
import { useMemo, useState } from 'react'

import { useLocation } from '@suntex.core/nav'
import {
  Database_Procs_SchoolSummaryByTeam_Item,
  Entities_Assessment_TeamSummary_PlayerSummary
} from '@suntex.fim/api-client'
import {
  AssessmentColumnType as ColumnType,
  getAssessmentReportData,
  useAssessmentSchoolSummaryQuery,
  useAssessmentTable,
  useAssessmentTeamSummaryQuery
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { EntityLevel } from '@suntex.fim/enums'
import { SendMessage } from '@suntex.fim/messages'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { ExportMenu, SearchInput } from '@suntex.fim/ui'

import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import {
  columns,
  hidableSelectLabel,
  schoolSummaryInitialSorting,
  schoolColumns,
  schoolHidableSelectLabel,
  teamSummaryInitialSorting
} from './AssessmentReport/AssessmentReportSummaryTable'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

/**
 * returns JSX for report summary
 */
function AssessmentReportSummary() {
  const location = useLocation()
  const team = useOptionalTeamInfo()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()
  const { login } = useAuth(isAuthenticated)
  const [searchQuery, setSearchQuery] = useState('')
  const [isPrintLoading, setIsPrintLoading] = useState(false)
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const isSchool =
    login?.entityLevel === EntityLevel.School &&
    location.pathname.includes('school')
  const apiParams = GetAPIparams(isSchool, dropdownFilters)

  const { id, year, grade } = apiParams

  const { data: teamReportData, isLoading: isTeamLoading } =
    useAssessmentTeamSummaryQuery(id, { enabled: !isSchool && id != null })

  const { data: schoolReportData, isLoading: isSchoolLoading } =
    useAssessmentSchoolSummaryQuery(id, year, grade, {
      enabled: isSchool && id != null
    })
  const assessmentReportData = getAssessmentReportData(teamReportData)
  const assessmentReportSchoolData = getAssessmentReportData(schoolReportData)

  const isLoading = isTeamLoading || isSchoolLoading || isPrintLoading
  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  // Memoize columns for each table type separately
  const memoTeamColumns = useMemo<
    ColumnType<Entities_Assessment_TeamSummary_PlayerSummary>[]
  >(() => columns({ sendMessage }), [])
  const memoSchoolColumns = useMemo<
    ColumnType<Database_Procs_SchoolSummaryByTeam_Item>[]
  >(() => schoolColumns(), [])

  const {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalStudents,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalStickers,
    totalStickersPerStudent,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalTimeOnTask,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalProblemsSolved,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalMathematicians,
    averagePercentMathematicians,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalGrandChampions,
    averagePercentGrandChampions
  } = calculateSummary(assessmentReportSchoolData)

  const footerValues = [
    null,
    null,
    null,
    null,
    null,
    totalStudents?.toLocaleString() || 0,
    totalStickers?.toLocaleString() || 0,
    totalStickersPerStudent >= 0 ? getFixedValue(totalStickersPerStudent) : '-',
    null,
    totalTimeOnTask?.toLocaleString() || 0,
    totalProblemsSolved?.toLocaleString() || 0,
    totalMathematicians?.toLocaleString() || 0,
    averagePercentMathematicians >= 0
      ? `${getFixedValue(averagePercentMathematicians)}%`
      : '-',
    totalGrandChampions?.toLocaleString() || 0,
    averagePercentMathematicians >= 0
      ? `${getFixedValue(averagePercentGrandChampions)}%`
      : '-'
  ]
  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const teamTable =
    useAssessmentTable<Entities_Assessment_TeamSummary_PlayerSummary>({
      data: assessmentReportData,
      columns: memoTeamColumns,
      isLoading,
      globalFilter: searchQuery,
      initialSorting: teamSummaryInitialSorting,
      displayFooter: false,
      footerValues: undefined
    })

  const schoolTable =
    useAssessmentTable<Database_Procs_SchoolSummaryByTeam_Item>({
      data: assessmentReportSchoolData,
      columns: memoSchoolColumns,
      isLoading,
      hideFirstTwoColumns: false,
      globalFilter: searchQuery,
      initialSorting: schoolSummaryInitialSorting,
      displayFooter: true,
      footerValues
    })

  // Choose the correct table and Table component based on isSchool
  const table = isSchool ? schoolTable.table : teamTable.table
  const Table = isSchool ? schoolTable.Table : teamTable.Table
  const selectedRow = table.getSelectedRowModel().flatRows

  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: any }) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      label: original.displayName || original.userId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: original.id
    })
  )
  const fileName = isSchool
    ? `${login.schoolName}_summary`
    : `${team?.name}_summary`
  const exportColumn = isSchool ? schoolHidableSelectLabel : hidableSelectLabel
  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool ? ['teamGrade'] : [],
    isSchool,
    setIsLoading: setIsPrintLoading
  })

  return (
    <Flex vertical gap="20px" style={{ position: 'relative' }}>
      {/* Loader overlay when loading and popup not open */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      <AssessmentReportNavigation
        exportButton={
          <ExportMenu
            ariaLabel="Export report options"
            items={items}
            trigger={['click']}
          />
        }
        dropdownFilters={dropdownFilters}
        setDropDownFilters={setDropDownFilters}
        isSchool={isSchool}
      />
      <AssessmentReportSummaryDashboard
        dropdownFilters={dropdownFilters}
        isSchool={isSchool}
      />
      {/* result */}
      <div className="main-result">
        {/* Updated toolbar design */}
        <Flex
          justify="space-between"
          align="center"
          className="main-result__toolbar"
          wrap
        >
          <h4 className="title">
            {isSchool ? 'School Teams' : 'Team Players'}
          </h4>

          <Flex gap="10px" justify="space-between" wrap>
            <div className="report-search">
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            {!isSchool && (
              <Button
                intent="tertiary"
                size="small"
                disabled={isDisabled}
                onClick={() => setIsModalOpen(true)}
                className="print-hidden"
                tabIndex={0}
                aria-disabled={isDisabled}
                aria-label="Send message to selected users"
                onKeyDown={(e) => {
                  if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                    setIsModalOpen(true)
                  }
                }}
              >
                SEND MESSAGE
              </Button>
            )}
          </Flex>
        </Flex>
        <Table />
      </div>
      {!isModalOpen && !isSchool && (
        <SelectedRowPopUp
          selectedRow={selectedRow}
          resetRowSelection={table.resetRowSelection}
          handleSendMessage={() => setIsModalOpen(true)}
        />
      )}
      <SendMessage
        isModalOpen={isModalOpen}
        closeModal={(isSuccess) => {
          setIsModalOpen(false)
          setSendMessageData([])
          if (isSuccess) {
            table.resetRowSelection()
          }
        }}
        selectedUserOptions={selectedUserOptions}
        recipientEntityId={team?.id}
      />
    </Flex>
  )
}

export default AssessmentReportSummary
